import type { CSSProperties, FC, ReactNode } from 'react'
import type { ReportSummary } from '@/types'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { formatDate } from '@/lib/format'
import { FigLabel } from '@/components/generals/fig-label'
import { Reveal } from '@/components/generals/reveal'
import { TextLink } from '@/components/generals/text-link'

interface HomeReportsProps {
    reports: Array<ReportSummary>
}

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

/**
 * Home section 11: the three latest field reports. Hovering a row opens its one-line abstract.
 */
export const HomeReports: FC<HomeReportsProps> = ({ reports }): ReactNode => {
    const rows = reports.slice(0, 3)

    if (rows.length === 0) return null

    return (
        <section id='field-reports' aria-labelledby='home-reports-title' className='relative border-t border-hairline'>
            <Reveal className='flex flex-wrap items-end justify-between gap-8 px-gutter pb-12 pt-20 desk:pb-16 desk:pt-28'>
                <div>
                    <FigLabel fig='11' title='Field reports' detail='Notes from site' note='Written by the partners who took the readings' />

                    <h2 id='home-reports-title' className='display-lg mt-10'>
                        <span className='reveal-line'>
                            <span className='reveal-inner text-ink' style={delay(80)}>
                                Notes from
                            </span>
                        </span>

                        <span className='reveal-line'>
                            <span className='reveal-inner text-dim' style={delay(170)}>
                                the field.
                            </span>
                        </span>
                    </h2>
                </div>

                <div className='reveal-fade'>
                    <TextLink href='/field-reports'>
                        All reports
                    </TextLink>
                </div>
            </Reveal>

            <ol className='border-t border-hairline'>
                {rows.map(report => (
                    <li key={report.id} className='group relative border-b border-hairline'>
                        <span className='absolute inset-y-0 left-0 w-[3px] origin-top scale-y-0 bg-signal transition-transform duration-500 ease-instrument group-focus-within:scale-y-100 group-hover:scale-y-100' aria-hidden='true' />

                        <Link
                            href={`/field-reports/${report.slug}`}
                            className='grid gap-x-8 gap-y-3 px-gutter py-8 desk:grid-cols-[9rem_6rem_minmax(0,1fr)_auto] desk:items-baseline desk:py-10'
                        >
                            <span className='hud text-mute'>
                                <time dateTime={report.publishedOn}>
                                    {formatDate(report.publishedOn)}
                                </time>
                            </span>

                            <span className='hud text-ink' data-mdlfy-cms={`field_reports:${report.id}:code`}>
                                {report.code}
                            </span>

                            <span className='min-w-0'>
                                <span
                                    className='block font-heading text-[clamp(26px,3vw,46px)] font-medium leading-[1.04] tracking-[-0.026em] text-ink transition-transform duration-500 ease-instrument desk:group-hover:translate-x-4'
                                    data-mdlfy-cms={`field_reports:${report.id}:title`}
                                >
                                    {report.title}
                                </span>

                                <span className='grid transition-[grid-template-rows] duration-500 ease-instrument desk:grid-rows-[0fr] desk:group-focus-within:grid-rows-[1fr] desk:group-hover:grid-rows-[1fr]'>
                                    <span className='overflow-hidden'>
                                        <span className='block pt-3 text-[15px] leading-[1.6] text-ink/75 transition-opacity duration-500 desk:opacity-0 desk:group-focus-within:opacity-100 desk:group-hover:opacity-100 desk:group-hover:translate-x-4 desk:transition-[opacity,transform]' data-mdlfy-cms={`field_reports:${report.id}:abstract`}>
                                            {report.abstract}
                                        </span>
                                    </span>
                                </span>
                            </span>

                            <ArrowUpRight className='hidden size-5 self-center text-ink transition-transform duration-500 ease-instrument group-hover:-translate-y-1 group-hover:translate-x-1 desk:block' strokeWidth={1.25} aria-hidden='true' />
                        </Link>
                    </li>
                ))}
            </ol>
        </section>
    )
}
